import { useState, useRef, useEffect } from "react";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { persistUserSession } from "../../utils/authSession";
import { appStorage } from "../../utils/appStorage";

export default function SignupOtp() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        email: "",
        otp: new Array(6).fill(""),
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [resendTimer, setResendTimer] = useState(30);
    const [canResend, setCanResend] = useState(false);
    const otpInputs = useRef([]);

    useEffect(() => {
        // Timer for resend code
        if (resendTimer > 0) {
            const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
            return () => clearTimeout(timer);
        } else {
            setCanResend(true);
        }
    }, [resendTimer]);



    const handleOtpChange = (element, index) => {
        if (isNaN(element.value)) return false;

        // Update OTP value
        const newOtp = [...formData.otp];
        newOtp[index] = element.value;
        setFormData(prev => ({ ...prev, otp: newOtp }));

        // Auto focus to next input
        if (element.value !== "" && element.nextSibling) {
            element.nextSibling.focus();
        }
    };

    const handleOtpKeyDown = (e, index) => {
        // Handle backspace key
        if (e.key === "Backspace" && e.target.value === "" && index > 0) {
            otpInputs.current[index - 1].focus();
        }
    };


    const handleSubmitOtp = async (e) => {
        e.preventDefault();

        const enteredOtp = formData.otp.join("");

        if (formData.otp.some((digit) => digit === "")) {
            setErrors({ otp: "Please enter the complete code" });
            return;
        }

        const email = appStorage.getItem("email");

        setErrors({});
        setLoading(true);

        try {
            let apiUrl = `${process.env.REACT_APP_API_BASE_URL}/verify-email`;
            let payload = new URLSearchParams({
                email: email,
                otp: enteredOtp,
            });
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: payload.toString(),
            });

            const data = await response.json();
            // Handle new API response: data.data[0] is user info, status is string
            const status = data.status;
            const message = data.message;

            const user = Array.isArray(data.data) ? data.data[0]?.user || data.data[0] : data.user;
            if (user && user.token) {
                Cookies.set("auth_token", user.token, { expires: 7, path: "/" });
            }

            if (user) {
                persistUserSession(user);
                if (Number(user.payment_status) === 1) {
                    appStorage.setItem("choose_plan_required", "0");
                    appStorage.setItem("upgrade_plan_required", "0");
                }
            }

            // If API explicitly returns a failure status (e.g. { status: "0", message: "Invalid email OTP" })
            if (status === "0" || status === 0) {
                toast.dismiss();
                toast.error(message || "Invalid OTP");
                setErrors({ otp: message || "Invalid OTP" });
                return;
            }

            if (status === "4") {
                toast.success(message || "OTP Verified Successfully! 🎉");
                setTimeout(() => {
                    navigate("/wait-list", { replace: true });
                }, 100);
            } else if (status === "2" && user?.email_verified === 0) {
                toast.success(message || "OTP Verified Successfully! 🎉");
                setTimeout(() => {
                    navigate("/otp", { replace: true });
                }, 100);
            } else if (status === "3") {
                toast.success(message || "OTP Verified Successfully! 🎉");
                setTimeout(() => {
                    navigate("/otp", { replace: true });
                }, 100);
            } else if (status === "5") {
                navigate("/payment", { replace: true });
            } else if (
                status === "1" &&
                (user?.sms_verified === 0 || user?.sms_verified === "0") &&
                user?.phone_number
            ) {
                appStorage.setItem("sms_verified", "0");
                appStorage.setItem("verification_type", "3");
                toast.success(message || "Email verified! Please verify your mobile number.");
                setTimeout(() => {
                    navigate("/otp", { replace: true });
                }, 100);
            } else if (
                status === "1" &&
                (user?.payment_status === 0 || user?.payment_status === "0")
            ) {
                navigate("/payment", { replace: true });
            } else if (status === "1" && user?.email_verified === 1) {
                toast.success(message || "OTP Verified Successfully! 🎉");
                setTimeout(() => {
                    navigate("/contact", { replace: true });
                }, 100);
            } else {
                throw new Error(message || "Unexpected login status");
            }
        } catch (error) {
            toast.dismiss();
            toast.error(error.message || "Something went wrong!");
            setErrors({ otp: error.message });
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        const email = appStorage.getItem("email");

        let apiUrl = `${process.env.REACT_APP_API_BASE_URL}/resend-verify-email-otp`;
        let payload = new URLSearchParams({ email });

        try {
            setLoading(true);

            const response = await fetch(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: payload.toString(),
            });

            const data = await response.json();

            if (response.ok) {
                toast.success("OTP resent successfully!");
                setResendTimer(30);
                setCanResend(false);
            } else {
                toast.dismiss();

                toast.error(data.message || "Failed to resend OTP");
            }

        } catch (error) {
            toast.dismiss();
            toast.error("Something went wrong!");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        handleResendOtp();
    }, []);

    return (
        <div className="mobile-scroll-y h-[var(--app-height,100dvh)] bg-[#F3F0FF] px-4 py-6 sm:py-8">
            <div className="mx-auto flex min-h-full w-full max-w-[520px] flex-col justify-center p-6 sm:p-8 md:p-10">
                {/* Logo + Title */}
                <div className="flex flex-col items-center gap-4 mb-8">
                    {/* Logo */}
                    <div className="h-20 w-20 rounded-xl bg-gradient-to-br from-[#7C4DFF] to-[#6A35FF] flex items-center justify-center shadow-md">
                        <img src="../logo.svg" alt=" PhoneBookPlus Logo" />
                    </div>

                    <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-500 font-inter">
                         PhoneBookPlus
                    </h1>
                </div>

                <div className="space-y-3 mb-3">
                    <div className="space-y-5">
                        {(() => {
                            const email = appStorage.getItem("email");
                            return (
                                <p className="text-[#475467] font-medium text-center">
                                    Sign in code has been sent to <span className="font-semibold">{email}</span> check your email to continue the sign in process.
                                </p>
                            );
                        })()}
                    </div>
                </div>

                {/* OTP Form */}
                <form className="space-y-5" onSubmit={handleSubmitOtp}>
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                            Enter Verification Code
                        </label>
                        <div className="flex justify-between space-x-2">
                            {formData.otp.map((data, index) => (
                                <input
                                    key={index}
                                    type="text"
                                    maxLength="1"
                                    value={data}
                                    ref={(ref) => (otpInputs.current[index] = ref)}
                                    onChange={e => handleOtpChange(e.target, index)}
                                    onKeyDown={e => handleOtpKeyDown(e, index)}
                                    onFocus={e => e.target.select()}
                                    className="w-12 h-12 rounded-xl border border-gray-400 bg-white text-center text-lg font-semibold text-gray-800 outline-none transition focus:border-[#7C4DFF] focus:ring-4 focus:ring-[#7C4DFF]/15 shadow-sm"
                                />
                            ))}
                        </div>
                        {errors.otp && <p className="text-red-500 text-xs mt-1">{errors.otp}</p>}
                    </div>

                    <div className="text-center">
                        <p className="text-sm text-gray-600">
                            Haven't received the sign in code?{" "}
                            {canResend ? (
                                <button
                                    type="button"
                                    onClick={handleResendOtp}
                                    className="text-[#7C4DFF] hover:underline font-medium"
                                >
                                    Resend it
                                </button>
                            ) : (
                                <span className="text-gray-500">
                                    Resend in {resendTimer}s
                                </span>
                            )}
                        </p>
                    </div>

                    <button
                        type="submit"
                        disabled={loading || formData.otp.some((digit) => digit === "")}
                        className="w-full rounded-3xl bg-gradient-to-b from-[#7c4ff9] to-[#47299c] py-3.5 text-white font-semibold shadow-lg shadow-[#7C4DFF]/25 hover:brightness-105 active:scale-[0.99] transition disabled:opacity-60"
                    >
                        Submit
                    </button>
                </form>
            </div>
        </div>
    );
}